import type { EffectInstance, EffectType } from '@/domain/types';

/**
 * Parameter descriptors.
 *
 * One declaration per parameter drives four things at once: the default value
 * written into a new effect, the range used to clamp values loaded from a file,
 * the control rendered in the inspector, and the normalised 0..1 <-> real-value
 * mapping used by automation. Keeping them in one place is what stops those
 * four from drifting apart.
 */

export type ParamScale = 'linear' | 'log';

export interface ParamDescriptor {
  id: string;
  label: string;
  min: number;
  max: number;
  default: number;
  unit: string;
  scale: ParamScale;
  /** Discrete parameters (filter type, ratio steps) render as a selector. */
  choices?: readonly { value: number; label: string }[];
  step?: number;
  /**
   * False for parameters that cannot be automated because changing them does
   * not write to an AudioParam — a filter's type, or a reverb size that has to
   * rebuild its impulse response. The automation UI hides these rather than
   * offering a lane that would do nothing.
   */
  automatable?: boolean;
}

/** True unless a descriptor opts out. */
export function isAutomatable(descriptor: ParamDescriptor): boolean {
  return descriptor.automatable !== false;
}

export interface EffectDescriptor {
  type: EffectType;
  label: string;
  /** Short line shown under the effect name in the picker. */
  summary: string;
  params: readonly ParamDescriptor[];
}

const MIX: ParamDescriptor = {
  id: 'mix',
  label: 'Mix',
  min: 0,
  max: 1,
  default: 0.3,
  unit: '',
  scale: 'linear',
};

export const FILTER_TYPES = [
  { value: 0, label: 'Low pass' },
  { value: 1, label: 'High pass' },
  { value: 2, label: 'Band pass' },
  { value: 3, label: 'Notch' },
] as const;

export const EFFECT_DESCRIPTORS: Record<EffectType, EffectDescriptor> = {
  eq3: {
    type: 'eq3',
    label: 'EQ',
    summary: 'Three-band shelving and peaking EQ',
    params: [
      { id: 'lowGain', label: 'Low', min: -24, max: 24, default: 0, unit: 'dB', scale: 'linear' },
      { id: 'lowFreq', label: 'Low freq', min: 30, max: 800, default: 120, unit: 'Hz', scale: 'log' },
      { id: 'midGain', label: 'Mid', min: -24, max: 24, default: 0, unit: 'dB', scale: 'linear' },
      { id: 'midFreq', label: 'Mid freq', min: 200, max: 8000, default: 1000, unit: 'Hz', scale: 'log' },
      { id: 'midQ', label: 'Mid Q', min: 0.2, max: 10, default: 1, unit: '', scale: 'log' },
      { id: 'highGain', label: 'High', min: -24, max: 24, default: 0, unit: 'dB', scale: 'linear' },
      { id: 'highFreq', label: 'High freq', min: 1500, max: 16000, default: 6000, unit: 'Hz', scale: 'log' },
    ],
  },
  compressor: {
    type: 'compressor',
    label: 'Compressor',
    summary: 'Dynamics control with makeup gain',
    params: [
      { id: 'threshold', label: 'Threshold', min: -60, max: 0, default: -18, unit: 'dB', scale: 'linear' },
      { id: 'ratio', label: 'Ratio', min: 1, max: 20, default: 4, unit: ':1', scale: 'log' },
      { id: 'attack', label: 'Attack', min: 0.001, max: 0.5, default: 0.01, unit: 's', scale: 'log' },
      { id: 'release', label: 'Release', min: 0.02, max: 2, default: 0.25, unit: 's', scale: 'log' },
      { id: 'knee', label: 'Knee', min: 0, max: 40, default: 12, unit: 'dB', scale: 'linear' },
      { id: 'makeup', label: 'Makeup', min: 0, max: 24, default: 0, unit: 'dB', scale: 'linear' },
    ],
  },
  reverb: {
    type: 'reverb',
    label: 'Reverb',
    summary: 'Convolution reverb with a synthesised tail',
    params: [
      { id: 'size', label: 'Size', min: 0.2, max: 8, default: 2, unit: 's', scale: 'log', automatable: false },
      { id: 'damping', label: 'Damping', min: 500, max: 16000, default: 6000, unit: 'Hz', scale: 'log', automatable: false },
      { id: 'predelay', label: 'Pre-delay', min: 0, max: 0.2, default: 0.02, unit: 's', scale: 'linear' },
      { ...MIX, default: 0.25 },
    ],
  },
  delay: {
    type: 'delay',
    label: 'Delay',
    summary: 'Feedback delay with tone control',
    params: [
      { id: 'time', label: 'Time', min: 0.01, max: 2, default: 0.375, unit: 's', scale: 'log' },
      { id: 'feedback', label: 'Feedback', min: 0, max: 0.95, default: 0.35, unit: '', scale: 'linear' },
      { id: 'tone', label: 'Tone', min: 400, max: 16000, default: 4000, unit: 'Hz', scale: 'log' },
      { ...MIX, default: 0.25 },
    ],
  },
  filter: {
    type: 'filter',
    label: 'Filter',
    summary: 'Resonant filter',
    params: [
      {
        id: 'type',
        label: 'Type',
        min: 0,
        max: 3,
        default: 0,
        unit: '',
        scale: 'linear',
        choices: FILTER_TYPES,
        step: 1,
        automatable: false,
      },
      { id: 'frequency', label: 'Frequency', min: 20, max: 20000, default: 2000, unit: 'Hz', scale: 'log' },
      { id: 'resonance', label: 'Resonance', min: 0.1, max: 20, default: 1, unit: 'Q', scale: 'log' },
    ],
  },
  limiter: {
    type: 'limiter',
    label: 'Limiter',
    summary: 'Brickwall ceiling for the master bus',
    params: [
      { id: 'ceiling', label: 'Ceiling', min: -24, max: 0, default: -0.3, unit: 'dB', scale: 'linear' },
      { id: 'release', label: 'Release', min: 0.01, max: 1, default: 0.1, unit: 's', scale: 'log' },
    ],
  },
  chorus: {
    type: 'chorus',
    label: 'Chorus',
    summary: 'Two detuned copies, one each side',
    params: [
      { id: 'rate', label: 'Rate', min: 0.05, max: 5, default: 0.8, unit: 'Hz', scale: 'log' },
      { id: 'depth', label: 'Depth', min: 0, max: 10, default: 3, unit: 'ms', scale: 'linear' },
      { id: 'spread', label: 'Spread', min: 0, max: 1, default: 1, unit: '', scale: 'linear' },
      { ...MIX, default: 0.5 },
    ],
  },
  wam: {
    type: 'wam',
    label: 'Web Audio Module',
    summary: 'A plugin loaded from a URL, in the open Web Audio Modules format',
    // The plugin describes its own parameters once it has loaded; see
    // `effectDescriptor`, which builds the list from what it said. The mix is
    // the host's, so any plugin can be blended in.
    params: [{ ...MIX, default: 1 }],
  },
  saturator: {
    type: 'saturator',
    label: 'Saturator',
    summary: 'Soft clip on the audio thread, with a tone control after it',
    params: [
      { id: 'drive', label: 'Drive', min: 1, max: 20, default: 4, unit: '', scale: 'log' },
      { id: 'tone', label: 'Tone', min: 1000, max: 20000, default: 12000, unit: 'Hz', scale: 'log' },
      { ...MIX, default: 1 },
    ],
  },
};

export function defaultParams(type: EffectType): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of EFFECT_DESCRIPTORS[type].params) out[p.id] = p.default;
  return out;
}

export function paramDescriptor(type: EffectType, paramId: string): ParamDescriptor | undefined {
  return EFFECT_DESCRIPTORS[type].params.find((p) => p.id === paramId);
}

/**
 * The descriptor for an insert as it is, not just its type: a hosted plugin's
 * parameters are whatever the plugin reported, kept on the instance, and its
 * label is the plugin's name.
 */
export function effectDescriptor(effect: EffectInstance): EffectDescriptor {
  if (effect.type !== 'wam') return EFFECT_DESCRIPTORS[effect.type];
  return {
    type: 'wam',
    label: effect.wamName ?? 'Web Audio Module',
    summary: EFFECT_DESCRIPTORS.wam.summary,
    params: [
      ...(effect.wamParameters ?? [])
        .filter((p) => p.id !== 'mix')
        .map((p) => ({
          id: p.id,
          label: p.label,
          min: p.min,
          max: p.max,
          default: p.default,
          unit: p.units,
          scale: 'linear' as const,
        })),
      ...EFFECT_DESCRIPTORS.wam.params,
    ],
  };
}

export function effectParamDescriptor(effect: EffectInstance, paramId: string): ParamDescriptor | undefined {
  return effectDescriptor(effect).params.find((p) => p.id === paramId);
}

/** Real parameter value -> normalised 0..1, honouring the parameter's scale. */
export function normaliseParam(descriptor: ParamDescriptor, value: number): number {
  const v = Math.min(descriptor.max, Math.max(descriptor.min, value));
  if (descriptor.scale === 'log' && descriptor.min > 0) {
    return Math.log(v / descriptor.min) / Math.log(descriptor.max / descriptor.min);
  }
  return (v - descriptor.min) / (descriptor.max - descriptor.min);
}

/** Normalised 0..1 -> real parameter value. */
export function denormaliseParam(descriptor: ParamDescriptor, normalised: number): number {
  const n = Math.min(1, Math.max(0, normalised));
  if (descriptor.scale === 'log' && descriptor.min > 0) {
    return descriptor.min * Math.pow(descriptor.max / descriptor.min, n);
  }
  const value = descriptor.min + n * (descriptor.max - descriptor.min);
  return descriptor.step ? Math.round(value / descriptor.step) * descriptor.step : value;
}

export function formatParamValue(descriptor: ParamDescriptor, value: number): string {
  if (descriptor.choices) {
    return descriptor.choices.find((c) => c.value === Math.round(value))?.label ?? String(value);
  }
  const abs = Math.abs(value);
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : abs >= 1 ? 2 : 3;
  const shown = descriptor.unit === 's' && abs < 1 ? `${(value * 1000).toFixed(0)} ms` : `${value.toFixed(decimals)}${descriptor.unit ? ` ${descriptor.unit}` : ''}`;
  return shown;
}

// ---------------------------------------------------------------------------
// Track parameters
// ---------------------------------------------------------------------------

/**
 * Channel-strip parameters are described the same way effect parameters are, so
 * automation, the inspector and the mixer all read one definition. Volume is
 * automated in linear amplitude rather than decibels because that is what the
 * fader itself moves in — automation that disagreed with the fader's own scale
 * would draw a curve that does not match what the user drew.
 */
export const TRACK_PARAM_DESCRIPTORS: readonly ParamDescriptor[] = [
  { id: 'volume', label: 'Volume', min: 0, max: 1.6, default: 1, unit: '', scale: 'linear' },
  { id: 'pan', label: 'Pan', min: -1, max: 1, default: 0, unit: '', scale: 'linear' },
];

export function trackParamDescriptor(paramId: string): ParamDescriptor | undefined {
  return TRACK_PARAM_DESCRIPTORS.find((p) => p.id === paramId);
}
